import { type CSSProperties, type FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { hasOngoingSubscriptionError } from "@jti/commerce/shared";
import { BREAKPOINT_MEDIUM, debounce, globalEventsHandler, useDirection } from "@jti/fe-common";
import { type Product } from "@jti/magento";
import { useSelector } from "@jti/store/reduxHooks";
import { selectFreeShippingThreshold } from "@jti/store/selectors/selectFreeShippingThreshold";
import { useOutsideClick } from "@jti/ui";

import { FreeShippingProgressBar } from "@checkout/components/FreeShippingProgressBar/FreeShippingProgressBar";
import { useShouldDisplayFreeShippingProgressBar } from "@checkout/hooks/useShouldDisplayFreeShippingProgressBar";
import getElementHeightWithMargins from "@checkout/utils/getElementHeightWithMargins";

import { CartMiniContent } from "../../components/CartMiniContent";
import { CartMiniFooter } from "../../components/CartMiniFooter";
import { CartMiniHeader, type CartMiniHeaderProps } from "../../components/CartMiniHeader";
import { calculatFrameHeight } from "../../utils/calculatFrameHeight";

import { CartIcon } from "./CartIcon";

import styles from "./CartMiniContentContainer.module.scss";

export interface CartMiniContentContainerProps extends CartMiniHeaderProps {
  itemsPending?: boolean;
  qtyPending?: boolean;
  items: Product[];
  opened?: boolean;
  warningStatus?: boolean;
  isLimitEnabled?: boolean;
  isOnCartPage: boolean;
  portalTarget?: HTMLElement | null;
  shopButtonLabel?: string;

  onOpen?(): void;
  onClose?(): void;
  onInteract?(): void;
  onRemoveItemClick(itemId: string): void;
  onItemQuantityChange(itemId: string, value: number): void;
  onCheckoutClick(): void;
  onRemoveItems(itemIds: string[]): Promise<void>;
}

type CartMiniCustomStyles = {
  maxHeight?: number;
  minHeight?: number;
};

type ScrollShadows = {
  header: boolean;
  footer: boolean;
};

const SCROLL_POSITION_TOLERANCE = 1;
export const CartMiniContentContainer: FC<CartMiniContentContainerProps> = ({
  portalTarget,
  itemsPending: pending = false,
  qtyPending = false,
  subtotal,
  items,
  opened,
  onOpen,
  onClose,
  itemsCount,
  onRemoveItemClick,
  onItemQuantityChange,
  onCheckoutClick,
  onRemoveItems,
  warningStatus,
  isLimitEnabled,
  isOnCartPage,
  shopButtonLabel,
}) => {
  const miniCartRef = useRef<HTMLDivElement>(null);
  const miniCartHeaderRef = useRef<HTMLDivElement>(null);
  const scrollableContentRef = useRef<HTMLDivElement>(null);
  const scrollAnimationFrameRef = useRef<number | null>(null);
  const portalTargetRef = useRef<HTMLElement | null>(null);
  const buyLimitation = !!(warningStatus && isLimitEnabled);
  const [fullScreenMinicart, setFullScreenMinicart] = useState<boolean>(false);
  const [customStyles, setCustomStyles] = useState<CartMiniCustomStyles>({});
  const [portalStyles, setPortalStyles] = useState<CSSProperties>({});
  const [scrollShadows, setScrollShadows] = useState<ScrollShadows>({ header: false, footer: false });
  const isCartEmpty = items?.length === 0;
  const isRtl = useDirection() === "rtl";
  const { isMobileFirstNavigationEnabled } = useConfig();

  useLayoutEffect(() => {
    portalTargetRef.current = portalTarget ?? document.body;
  }, [portalTarget]);

  const freeShippingThreshold = useSelector(selectFreeShippingThreshold);
  const cartPrices = useSelector((state) => state.cart.prices.data);
  const keepVisibleWhenInitiallyReachedRef = useRef(false);

  const { shouldDisplayFreeShippingProgressBar } = useShouldDisplayFreeShippingProgressBar(cartPrices);

  if (
    !keepVisibleWhenInitiallyReachedRef.current &&
    subtotal?.value != null &&
    freeShippingThreshold != null &&
    subtotal.value < freeShippingThreshold
  ) {
    keepVisibleWhenInitiallyReachedRef.current = true;
  }
  const keepVisibleWhenInitiallyReached = keepVisibleWhenInitiallyReachedRef.current;

  const freeShippingProgressBar = useMemo(() => {
    if (!shouldDisplayFreeShippingProgressBar) {
      return null;
    }

    return (
      <FreeShippingProgressBar
        current={subtotal?.value}
        currency={subtotal?.currency}
        threshold={freeShippingThreshold}
        keepVisibleWhenInitiallyReached={keepVisibleWhenInitiallyReached}
      />
    );
  }, [
    shouldDisplayFreeShippingProgressBar,
    subtotal?.value,
    subtotal?.currency,
    freeShippingThreshold,
    keepVisibleWhenInitiallyReached,
  ]);

  const classes = classNames(
    styles.miniCart,
    opened && styles["miniCart--open"],
    items.length > 0 ? styles["miniCart--with-items"] : styles["miniCart--no-items"],
    fullScreenMinicart && styles["miniCart--fullscreen"]
  );

  useEffect(() => {
    globalEventsHandler.getCartActive().dispatch({
      active: (opened || isOnCartPage) && !pending,
    });
  }, [opened, isOnCartPage, pending]);

  const onClickLabel = () => {
    if (!pending) {
      if (!opened && onOpen) {
        onOpen();
      } else if (onClose && opened) {
        onClose();
      }
    }
  };

  useOutsideClick(
    miniCartRef,
    () => {
      if (onClose) {
        onClose();
      }
    },
    { excludeSelectors: ['[data-js-selector="mini-cart-content"]'] }
  );

  const handleFullScreenMinicart = () => {
    const height = getElementHeightWithMargins(miniCartHeaderRef);
    setFullScreenMinicart(height > window.innerHeight);
  };

  const updatePortalPosition = () => {
    if (!miniCartRef.current) return;
    const rect = miniCartRef.current.getBoundingClientRect();
    const isDesktop = window.innerWidth >= BREAKPOINT_MEDIUM;

    if (isDesktop) {
      // `inset-inline-end` resolves to `left` in RTL and to `right` in LTR,
      // so the offset has to be measured from the matching viewport edge.
      setPortalStyles({
        top: rect.bottom,
        insetInlineEnd: isRtl ? rect.left : window.innerWidth - rect.right,
      });
    } else {
      setPortalStyles({});
    }
  };
  // TODO if this will be used in other places - create a hook or util
  // https://jira.jtiweb.co.uk/browse/DESP-157118
  const updateScrollShadows = useCallback(() => {
    if (!scrollableContentRef.current) {
      return;
    }

    const { clientHeight, scrollHeight, scrollTop } = scrollableContentRef.current;
    const maximumScrollTop = scrollHeight - clientHeight;
    const nextScrollShadows = {
      header: scrollTop > SCROLL_POSITION_TOLERANCE,
      footer: maximumScrollTop > SCROLL_POSITION_TOLERANCE && scrollTop < maximumScrollTop - SCROLL_POSITION_TOLERANCE,
    };

    setScrollShadows((currentScrollShadows) =>
      currentScrollShadows.header === nextScrollShadows.header &&
      currentScrollShadows.footer === nextScrollShadows.footer
        ? currentScrollShadows
        : nextScrollShadows
    );
  }, []);

  const scheduleScrollShadowUpdate = useCallback(() => {
    if (scrollAnimationFrameRef.current !== null) {
      return;
    }

    scrollAnimationFrameRef.current = window.requestAnimationFrame(() => {
      scrollAnimationFrameRef.current = null;
      updateScrollShadows();
    });
  }, [updateScrollShadows]);

  const cancelScrollShadowUpdate = useCallback(() => {
    if (scrollAnimationFrameRef.current === null) {
      return;
    }

    window.cancelAnimationFrame(scrollAnimationFrameRef.current);
    scrollAnimationFrameRef.current = null;
  }, []);

  useLayoutEffect(() => {
    if (!opened || pending) {
      cancelScrollShadowUpdate();
      setScrollShadows({ header: false, footer: false });
      return;
    }

    updateScrollShadows();

    let resizeObserver: ResizeObserver | undefined;

    if (typeof ResizeObserver !== "undefined" && scrollableContentRef.current) {
      resizeObserver = new ResizeObserver(scheduleScrollShadowUpdate);
      resizeObserver.observe(scrollableContentRef.current);
    }

    return () => {
      resizeObserver?.disconnect();
      cancelScrollShadowUpdate();
    };
  }, [
    cancelScrollShadowUpdate,
    fullScreenMinicart,
    items.length,
    opened,
    pending,
    scheduleScrollShadowUpdate,
    updateScrollShadows,
  ]);

  useEffect(() => {
    if (opened && !pending) {
      handleFullScreenMinicart();
    } else {
      setFullScreenMinicart(false);
    }
    setCustomStyles(calculatFrameHeight(miniCartRef));
    updatePortalPosition();
  }, [opened, pending]);

  useEffect(() => {
    const updateFrameHeight = () => {
      setCustomStyles(calculatFrameHeight(miniCartRef));
    };

    const handleResize = () => {
      updateFrameHeight();
      handleFullScreenMinicart();
      updatePortalPosition();
    };

    const handleResizeDebounced = debounce(handleResize, 250);

    handleResize();

    window.addEventListener("resize", handleResizeDebounced);
    return () => window.removeEventListener("resize", handleResizeDebounced);
  }, []);

  useEffect(() => {
    const onClickEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && onClose) {
        onClose();
      }
    };

    window.document.addEventListener("keydown", onClickEscape);
    return () => window.document.removeEventListener("keydown", onClickEscape);
  }, [onClose]);

  const miniCartPanel = (
    <div className={classes} data-js-selector="mini-cart-content" data-testid={"mini-cart-header"} style={portalStyles}>
      <div className={styles.miniCartContainer} style={customStyles}>
        <CartMiniHeader
          subtotal={subtotal}
          itemsCount={itemsCount}
          onClose={onClose}
          hasShadow={scrollShadows.header}
          pending={pending}
        />
        <CartMiniContent
          ref={scrollableContentRef}
          pending={pending}
          items={items}
          qtyPending={qtyPending}
          buyLimitation={buyLimitation}
          freeShippingProgressBar={freeShippingProgressBar}
          onRemoveItemClick={onRemoveItemClick}
          onItemQuantityChange={onItemQuantityChange}
          onRemoveItems={onRemoveItems}
          onScroll={scheduleScrollShadowUpdate}
        />
        <CartMiniFooter
          disableCheckoutButton={buyLimitation || qtyPending || hasOngoingSubscriptionError(items)}
          onCheckoutClick={onCheckoutClick}
          isCartEmpty={isCartEmpty}
          hasShadow={scrollShadows.footer}
          isVisible={!isCartEmpty || Boolean(shopButtonLabel)}
          shopButtonLabel={shopButtonLabel}
        />
      </div>
    </div>
  );

  return (
    <div className={styles.container} ref={miniCartRef} data-testid={"cart"}>
      <CartIcon
        disabled={pending}
        onClick={onClickLabel}
        label={itemsCount > 0 ? itemsCount : undefined}
        isOpen={opened || isOnCartPage}
        isExpanded={opened}
        desktopSize={isMobileFirstNavigationEnabled ? "md" : "sm"}
      />
      {portalTargetRef.current && createPortal(miniCartPanel, portalTargetRef.current)}
    </div>
  );
};
